/**
 * "Remove my profile" (SPEC §23.1–23.2), public at app.<domain>/remove and linked from the main website.
 * The creator chooses what they want, passes Cloudflare Turnstile and gets a verification code to put in their
 * Instagram bio (or send by DM). The team verifies and applies the request; nothing changes automatically.
 */
import { useState, type FormEvent } from 'react';
import { CheckCircle2, ShieldCheck } from 'lucide-react';
import { BRAND } from '@vw/core/src/brand';
import { Button, Input } from '@vw/ui';
import { api, ApiError } from '../../api';
import { PublicShell } from './PublicShell';
import { useTurnstile } from './turnstile';

const OPTIONS = [
  { id: 'hide_listing', label: 'Hide me from listings', help: 'No Creator Library, search or public pages.' },
  { id: 'stop_collection', label: 'Stop collecting my profile', help: 'Future scans of your profile are refused.' },
  { id: 'delete_data', label: 'Delete my stored data', help: 'Videos, thumbnails, avatar, transcripts and metrics.' },
] as const;

export function Remove() {
  const { siteKey, token, blocked, box, reset } = useTurnstile('remove');
  const [actions, setActions] = useState<Set<string>>(new Set(['hide_listing', 'stop_collection', 'delete_data']));
  const [handle, setHandle] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ code: string; handle: string } | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ code: string; handle: string }>('/api/public/removal-requests', { method: 'POST', json: { handle, email, actions: [...actions], message, turnstileToken: token } });
      setDone(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
      reset();
    } finally {
      setBusy(false);
    }
  }

  const toggle = (id: string) => { const n = new Set(actions); n.has(id) ? n.delete(id) : n.add(id); setActions(n); };
  return (
    <PublicShell cta={null}>
      <div className="mx-auto max-w-xl space-y-6">
        <div className="space-y-2">
          <h1 className="text-3xl font-black tracking-tight">Remove my profile</h1>
          <p className="text-sm text-muted-foreground">Are you the creator? Ask us to hide your profile, stop collecting it, or delete what we store. We verify that the Instagram account is yours before acting, so nobody can remove someone else.</p>
        </div>
        {done ? (
          <section className="space-y-4 rounded-3xl border border-emerald-500/30 bg-emerald-500/5 p-6">
            <p className="flex items-center gap-2 font-bold text-emerald-700 dark:text-emerald-400"><CheckCircle2 className="h-5 w-5" />Request received for @{done.handle}</p>
            <p className="text-sm">To prove the account is yours, do <b>one</b> of these:</p>
            <ol className="list-decimal space-y-1 pl-5 text-sm">
              <li>Add this code to your Instagram bio for up to 48 hours, or</li>
              <li>Send it to us in an Instagram DM from @{done.handle}.</li>
            </ol>
            <div className="rounded-2xl bg-card p-4 text-center font-mono text-3xl font-black tracking-widest">{done.code}</div>
            <p className="text-xs text-muted-foreground">Keep this page or note the code. Sending the form again for the same handle and email gives another code that works too, but doesn't change the request; to change what you asked for, email us. We check requests by hand and reply to your email; you can remove the code from your bio once we confirm. Questions: {BRAND.supportEmail}.</p>
          </section>
        ) : (
          <form onSubmit={submit} className="space-y-5 rounded-3xl border border-border bg-card p-6 shadow-xs">
            <label className="block text-sm font-semibold">Instagram handle
              <Input className="mt-1.5" value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="@yourhandle" required maxLength={80} />
            </label>
            <label className="block text-sm font-semibold">Your email
              <Input className="mt-1.5" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required maxLength={200} />
            </label>
            <fieldset className="space-y-2">
              <legend className="mb-1 text-sm font-semibold">What would you like us to do?</legend>
              {OPTIONS.map((o) => (
                <label key={o.id} className="flex cursor-pointer items-start gap-3 rounded-xl border border-border p-3">
                  <input type="checkbox" className="mt-0.5" checked={actions.has(o.id)} onChange={() => toggle(o.id)} />
                  <span className="text-sm"><b>{o.label}</b><span className="block text-xs text-muted-foreground">{o.help}</span></span>
                </label>
              ))}
            </fieldset>
            <label className="block text-sm font-semibold">Message (optional)
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={2000} rows={3}
                className="mt-1.5 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
            </label>
            {siteKey && <div ref={box} />}
            {blocked && <p className="rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">The anti-spam check couldn't load (an ad blocker can stop it). Allow challenges.cloudflare.com, or email {BRAND.supportEmail} with your handle and request.</p>}
            {siteKey === null && <p className="text-xs text-muted-foreground">If the form doesn't work, email {BRAND.supportEmail} with your handle and request.</p>}
            {error && <p role="alert" className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
            <Button type="submit" size="lg" className="w-full" loading={busy} disabled={!actions.size || (!!siteKey && !token)}><ShieldCheck className="h-4 w-4" />Send request</Button>
            <p className="text-xs text-muted-foreground">We use your email only to handle this request.</p>
          </form>
        )}
      </div>
    </PublicShell>
  );
}
